import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation,
  afterNextRender,
  inject,
  signal,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CountryToken, EnvToken } from '@app/core/env.tokens';
import { StateService } from '@services/logic/state.service';
import { LocalizationService } from '@services/logic/localization.service';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { HandyhanderTypeInterface } from '@app/components/seoPageElements/meet-some-handyhands/interface/handyhander.type.interface';
import { DecimalPipe, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';

@Component({
  imports: [TranslatePipe, MatIconModule, DecimalPipe],
  providers: [TranslatePipe, StateService, Router],
  encapsulation: ViewEncapsulation.Emulated,
  selector: 'app-meet-some-handyhands',
  templateUrl: './meet-some-handyhands.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./meet-some-handyhands.component.scss'],
})
export class MeetSomeHandyhandsComponent implements OnDestroy {
  protected readonly env = inject(EnvToken);
  private readonly country = inject(CountryToken);
  protected readonly statistics = inject(LocalizationService).statistics;

  @ViewChild('carouselContainer')
  carouselContainerRef!: ElementRef<HTMLDivElement>;

  public handyhandPickDesktop = `${this.env.cloudFrontImgUrl}/private/lp/handyhand-large_123.webp`;
  public handyhandPickMobile = `${this.env.cloudFrontImgUrl}/private/lp/handyhand-smaller_123.webp`;
  public activeHandyhandImg: string = '';
  public starIcon = '/assets/svg/star_vector.svg';
  private _innerWidth: number = 480;
  private isBrowser: boolean = false;
  public isMobile: boolean = true;
  public handyhanders: HandyhanderTypeInterface[] = [];

  protected readonly showLeftArrow = signal(false);
  protected readonly showRightArrow = signal(false);
  private timeoutIds: ReturnType<typeof setTimeout>[] = [];
  private resizeListener?: () => void;

  defaultImage = '/assets/svg/logo.svg';
  mitId = 'assets/svg/mit_id.svg';
  mobileBadge = 'assets/svg/badges/phone.svg';
  dealBadge = 'assets/svg/icons/deal.svg';

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    public router: Router,
    public translatePipe: TranslatePipe,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    this.handyhanders = [
      {
        name: this.translatePipe.transform('PAGES.LANDING.HH_NAME_1'),
        imageSrc: `${this.env.cloudFrontImgUrl}/private/lp/webp/lp_hero_imgs/handyman_carsten.webp`,
        specialityTranslationKey: 'PAGES.LANDING.HH_SPECIALITY_1',
        userDescriptionKey: 'PAGES.LANDING.HH_DESC_1',
        trustRating: '4.9',
        completionRate: '100',
        reviewTranslationKey1: 'PAGES.LANDING.HH_WHAT_OTHERS_SAY',
        reviewTranslationKey2: 'PAGES.LANDING.HH_WHAT_OTHERS_SAY_1',
        reviewGiver: 'Simon P.',
        reviewGiven: 83,
        reviewReceived: 83,
        badges: this.buildBadges(),
      },
      {
        name: this.translatePipe.transform('PAGES.LANDING.HH_NAME_2'),
        imageSrc: `${this.env.cloudFrontImgUrl}/private/lp/webp/lp_hero_imgs/cleaner_lisa.webp`,
        specialityTranslationKey: 'PAGES.LANDING.HH_SPECIALITY_2',
        userDescriptionKey: 'PAGES.LANDING.HH_DESC_2',
        trustRating: '5.0',
        completionRate: '99',
        reviewTranslationKey1: 'PAGES.LANDING.HH_WHAT_OTHERS_SAY1',
        reviewTranslationKey2: 'PAGES.LANDING.HH_WHAT_OTHERS_SAY_2',
        reviewGiven: 73,
        reviewReceived: 73,
        reviewGiver: 'Jan L.',
        badges: this.buildBadges(),
      },
      {
        name: this.translatePipe.transform('PAGES.LANDING.HH_NAME_3'),
        imageSrc: `${this.env.cloudFrontImgUrl}/private/lp/webp/lp_hero_imgs/painter_gustav.webp`,
        specialityTranslationKey: 'PAGES.LANDING.HH_SPECIALITY_3',
        userDescriptionKey: 'PAGES.LANDING.HH_DESC_3',
        trustRating: '5.0',
        completionRate: '96',
        reviewTranslationKey1: 'PAGES.LANDING.HH_WHAT_OTHERS_SAY2',
        reviewTranslationKey2: 'PAGES.LANDING.HH_WHAT_OTHERS_SAY_3',
        reviewGiver: 'Lene K.',
        reviewGiven: 32,
        reviewReceived: 32,
        badges: this.buildBadges(),
      },
    ];
    afterNextRender(() => {
      this.updateWindowDimensions();
      this.updateBadgesForMarket();
      this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 100));
      this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 300));
      this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 500));

      if (this.isBrowser) {
        this.resizeListener = () => {
          this.updateWindowDimensions();
          this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 100));
        };
        window.addEventListener('resize', this.resizeListener);
      }
    });
  }

  ngOnDestroy(): void {
    this.timeoutIds.forEach((id) => clearTimeout(id));
    this.timeoutIds = [];

    if (typeof window !== 'undefined' && this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  /**
   * Build badges array based on current market
   * MitID is only available in DK market
   */
  private buildBadges(): {
    src: string;
    alt: string;
    height: number;
    width: number;
  }[] {
    const showMitId = this.country === 'dk';
    const badges: {
      src: string;
      alt: string;
      height: number;
      width: number;
    }[] = [];
    if (showMitId) {
      badges.push({
        src: this.mitId,
        alt: this.translatePipe.transform('PAGES.VERTICAL.MIT_ID'),
        height: 16,
        width: 58,
      });
    }
    badges.push({
      src: this.mobileBadge,
      alt: this.translatePipe.transform('PAGES.VERTICAL.MOBILE'),
      height: 34,
      width: 34,
    });
    return badges;
  }

  /**
   * Update badges for all handyhanders based on market
   * Called after hydration to ensure correct market detection
   */
  private updateBadgesForMarket(): void {
    const newBadges = this.buildBadges();
    this.handyhanders.forEach((hh) => {
      hh.badges = newBadges;
    });
  }

  private updateWindowDimensions() {
    if (this.isBrowser) {
      this._innerWidth = window.innerWidth;
      this.isMobile = this._innerWidth <= 900;
      this.updateActiveHandyhandImg();
    }
  }

  updateActiveHandyhandImg() {
    this.activeHandyhandImg = this.isMobile
      ? this.handyhandPickMobile
      : this.handyhandPickDesktop;
  }

  checkScrollArrows(): void {
    if (!isPlatformBrowser(this.platformId) || !this.carouselContainerRef) {
      return;
    }

    const container = this.carouselContainerRef.nativeElement;
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    if (scrollWidth === 0 || clientWidth === 0) {
      this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 50));
      return;
    }

    this.showLeftArrow.set(scrollLeft > 0);
    this.showRightArrow.set(scrollLeft < scrollWidth - clientWidth - 1);
  }

  scrollLeft(): void {
    if (!isPlatformBrowser(this.platformId) || !this.carouselContainerRef) {
      return;
    }

    const container = this.carouselContainerRef.nativeElement;
    const scrollAmount = 300;
    container.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    });

    this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 300));
  }

  scrollRight(): void {
    if (!isPlatformBrowser(this.platformId) || !this.carouselContainerRef) {
      return;
    }

    const container = this.carouselContainerRef.nativeElement;
    const scrollAmount = 300;
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });

    this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 300));
  }
}
